import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Show, SignIn, SignUp } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useI18n } from "@/lib/i18n";
import { ShopProvider } from "@/lib/store";
import { useFetch } from "@/lib/api";
import Header from "@/components/Header";
import Footer, { CookieBanner } from "@/components/Footer";
import { Loading } from "@/components/Common";
import Home from "@/pages/Home";
import Listing from "@/pages/Listing";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout, { CheckoutResult } from "@/pages/Checkout";
import Account, { OrderDetail } from "@/pages/Account";
import { Wishlist, Compare } from "@/pages/Lists";
import { ContentPage, Guides, Guide, Faq, Contact, TrackOrder } from "@/pages/Content";

const Admin = lazy(() => import("@/pages/admin/Admin"));

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Layout = () => {
  const { lang, setFx } = useI18n();
  const { data: settings } = useFetch(`/settings/public?lang=${lang}`, { cacheable: true });
  const fxRates = settings?.fx_rates, fxMap = settings?.lang_currency;
  useEffect(() => { if (fxRates && fxMap) setFx({ rates: fxRates, map: fxMap }); }, [fxRates, fxMap, setFx]);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-3 focus:bg-white">Skip to content</a>
      <Header settings={settings} />
      <main id="main" className="flex-1"><Outlet context={{ settings }} /></main>
      <Footer settings={settings} />
      <CookieBanner />
    </div>
  );
};

const Private = ({ children }) => (<><Show when="signed-in">{children}</Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>);
const AuthWrap = ({ children }) => <div className="container-hw py-16 flex items-center justify-center min-h-[60vh]">{children}</div>;

function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <BrowserRouter>
          <ClerkAuthProvider>
            <ShopProvider>
              <ScrollTop />
              <Toaster position="top-center" />
              <Routes>
                <Route path="/admin/*" element={<Private><Suspense fallback={<Loading />}><Admin /></Suspense></Private>} />
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Listing mode="all" />} />
                  <Route path="/c/:slug" element={<Listing mode="category" />} />
                  <Route path="/room/:slug" element={<Listing mode="room" />} />
                  <Route path="/style/:slug" element={<Listing mode="style" />} />
                  <Route path="/search" element={<Listing mode="search" />} />
                  <Route path="/sale" element={<Listing mode="sale" />} />
                  <Route path="/new" element={<Listing mode="new" />} />
                  <Route path="/bestsellers" element={<Listing mode="bestsellers" />} />
                  <Route path="/p/:slug" element={<Product />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutResult kind="success" />} />
                  <Route path="/checkout/cancel" element={<CheckoutResult kind="cancel" />} />
                  <Route path="/sign-in/*" element={<AuthWrap><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthWrap>} />
                  <Route path="/sign-up/*" element={<AuthWrap><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthWrap>} />
                  <Route path="/reset-password" element={<Navigate to="/sign-in" replace />} />
                  <Route path="/account" element={<Private><Account /></Private>} />
                  <Route path="/account/orders/:number" element={<Private><OrderDetail /></Private>} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/inspiration" element={<Guides />} />
                  <Route path="/inspiration/:slug" element={<Guide />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/track-order" element={<TrackOrder />} />
                  {["about", "shipping", "returns", "privacy", "terms", "cookies", "warranty", "assembly", "care"].map((s) => <Route key={s} path={`/${s}`} element={<ContentPage slug={s} />} />)}
                  <Route path="*" element={<ContentPage slug="__404" />} />
                </Route>
              </Routes>
            </ShopProvider>
          </ClerkAuthProvider>
        </BrowserRouter>
      </LangProvider>
    </HelmetProvider>
  );
}

export default App;
